// ─── API Configuration & Base Client ──────────────────────────────────────────
// Centralized API configuration for all backend endpoints

import { auth } from "../config/firebaseConfig";

// Automatically use localhost during development.
const API_BASE_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8000"
  : process.env.REACT_APP_API_URL || "http://localhost:8000";

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: 30000,
  ENDPOINTS: {
    INVITATIONS: {
      SEND: "/api/invitations/send",
      ACCEPT: "/api/invitations/accept",
      VERIFY: (token) => `/api/invitations/verify/${encodeURIComponent(token)}`,
    },
  },
};

/**
 * Base API client with Firebase authentication token management.
 */
export class APIClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL.replace(/\/$/, "");
    this.token = null;
  }

  /**
   * Set authentication token manually.
   */
  setToken(token) {
    this.token = token;
  }

  /**
   * Clear authentication token.
   */
  clearToken() {
    this.token = null;
  }

  /**
   * Get default headers.
   *
   * Content-Type is intentionally omitted for FormData because
   * the browser must set the multipart boundary automatically.
   */
  getHeaders(contentType = "application/json") {
    const headers = {};

    if (contentType) {
      headers["Content-Type"] = contentType;
    }

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Get the current Firebase ID token.
   *
   * Firebase automatically refreshes the token when required.
   */
  async refreshToken() {
    try {
      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        this.token = null;
        return null;
      }

      const freshToken = await firebaseUser.getIdToken(false);
      this.token = freshToken;

      return freshToken;
    } catch (error) {
      console.warn("Failed to get Firebase ID token:", error);
      this.token = null;
      return null;
    }
  }

  /**
   * Parse the response safely.
   *
   * Some endpoints may return an empty response or non-JSON content.
   */
  async parseResponse(response) {
    const contentType = response.headers.get("content-type") || "";

    if (response.status === 204) {
      return null;
    }

    if (contentType.includes("application/json")) {
      return response.json();
    }

    const text = await response.text();

    if (!text) {
      return null;
    }

    return text;
  }

  /**
   * Generic fetch wrapper with:
   * - Firebase token management
   * - request timeout
   * - JSON/FormData support
   * - consistent error handling
   */
  async request(endpoint, options = {}) {
    const {
      method = "GET",
      body = null,
      contentType = "application/json",
      isFormData = false,
      timeout = API_CONFIG.TIMEOUT,
      skipAuth = false,
    } = options;

    // Get the latest valid Firebase token before the request.
    if (!skipAuth) {
      await this.refreshToken();
    }

    const url = `${this.baseURL}${endpoint}`;

    // Do not set Content-Type manually for FormData.
    const headers = isFormData
      ? {}
      : this.getHeaders(contentType);

    // Add Authorization header if we have a token.
    if (!skipAuth && this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    const config = {
      method,
      headers,
      signal: controller.signal,
    };

    // Only add a body when one was actually provided.
    if (body !== null && body !== undefined) {
      config.body = isFormData ? body : JSON.stringify(body);
    }

    try {
      const response = await fetch(url, config);
      const data = await this.parseResponse(response);

      if (!response.ok) {
        const message =
          data?.detail ||
          data?.message ||
          data?.error ||
          `Request failed with status ${response.status}`;

        const error = new Error(message);

        error.status = response.status;
        error.data = data;

        throw error;
      }

      return {
        status: response.status,
        data,
      };
    } catch (error) {
      // Preserve our API errors.
      if (error.status) {
        throw error;
      }

      // Handle timeout.
      if (error.name === "AbortError") {
        const timeoutError = new Error(
          `Request timed out after ${timeout}ms`
        );

        timeoutError.status = 408;
        timeoutError.data = null;

        throw timeoutError;
      }

      // Handle network/connection errors.
      const networkError = new Error(
        error.message || "Network error. Please check your connection."
      );

      networkError.status = 0;
      networkError.data = null;

      throw networkError;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * GET request
   */
  get(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "GET",
    });
  }

  /**
   * POST request
   */
  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body,
    });
  }

  /**
   * POST request with FormData
   */
  postFormData(endpoint, formData, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body: formData,
      isFormData: true,
    });
  }

  /**
   * PUT request
   */
  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "PUT",
      body,
    });
  }

  /**
   * DELETE request
   */
  delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "DELETE",
    });
  }
}

// Singleton instance
export const apiClient = new APIClient();
